import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Row, Typography } from 'antd';
import { TbPlus, TbWebhook } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
    EmptySection,
    VirtualizedList as List,
    OneTimeCredentialModal,
    PlanTag,
    SkeletonList,
    type OneTimeCredentialDisclosure,
} from '@/shared';

import { maskCredential } from '../../components/credentialPreview';
import WebhookModal from './CreateWebhookModal';
import WebhookListItem, { WebhookListRecord } from './WebhookListItem';

const Webhooks: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);

    const [webhooks, setWebhooks] = useState<WebhookListRecord[]>([]);
    const [credentialDisclosure, setCredentialDisclosure] = useState<OneTimeCredentialDisclosure>();
    const [query, setQuery] = useState('');
    const filteredWebhooks = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return webhooks;
        }

        return webhooks.filter(({ name, endpoint }) =>
            [name, endpoint].some((value) => value?.toLowerCase().includes(normalizedQuery)),
        );
    }, [query, webhooks]);

    useEffect(() => {
        let active = true;

        authorizedAxiosInstance
            .get<WebhookListRecord[]>('/api/webhooks')
            .then(({ data }) => {
                if (active) {
                    setWebhooks(data);
                }
            })
            .catch(captureException)
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    const { hasFeature } = useEntitlements();
    const canUseWebhooks = hasFeature('apiAndWebhooks');

    const { token } = useAntDesignTheme();

    return (
        <>
            <Row align='top' wrap={false}>
                <Col flex='auto'>
                    <Typography.Title level={5}>
                        <TbWebhook size='1rem' style={{ verticalAlign: '-0.125rem', marginRight: '0.5rem' }} />
                        Webhooks
                        <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Subscribe to real-time events on Countercyclical.
                    </Typography.Paragraph>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    <Input
                        allowClear
                        value={query}
                        placeholder='Search Webhooks...'
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </Col>
                <Col flex='none'>
                    <Button onClick={() => setOpen(true)} type='dashed' disabled={!canUseWebhooks}>
                        <TbPlus size='1rem' style={{ verticalAlign: '-0.15rem' }} />
                        Add Webhook
                    </Button>
                </Col>
            </Row>
            <WebhookModal
                open={open}
                setOpen={setOpen}
                onCreated={(webhook) => {
                    const record = webhook as WebhookListRecord;
                    const signingSecretPreview = record.signingSecretPreview || maskCredential(webhook.signingSecret);

                    setWebhooks((items) => [
                        ...items,
                        { ...record, signingSecret: signingSecretPreview, signingSecretPreview },
                    ]);
                    setCredentialDisclosure({
                        copyLabel: 'Copy Signing Secret',
                        description:
                            'Use this signing secret to verify webhook requests. Copy it before closing this modal because Countercyclical cannot display it again.',
                        title: 'Webhook Created',
                        values: [{ label: 'Signing Secret', value: webhook.signingSecret }],
                    });
                }}
            />
            <OneTimeCredentialModal
                disclosure={credentialDisclosure}
                onClose={() => setCredentialDisclosure(undefined)}
            />
            {filteredWebhooks.length !== 0 && !loading ? (
                <>
                    <List
                        dataSource={filteredWebhooks}
                        renderItem={(wh: WebhookListRecord) => {
                            return (
                                <WebhookListItem
                                    key={wh?.id}
                                    item={wh}
                                    onDeleted={(webhookId) =>
                                        setWebhooks((items) => items.filter((item) => item?.id !== webhookId))
                                    }
                                    onUpdated={(updatedWebhook) =>
                                        setWebhooks((items) =>
                                            items.map((item) =>
                                                item.id === updatedWebhook.id ? updatedWebhook : item,
                                            ),
                                        )
                                    }
                                />
                            );
                        }}
                    />
                </>
            ) : loading ? (
                <SkeletonList listLength={8} maxHeight='20rem' minHeight='20rem' />
            ) : (
                <EmptySection
                    height='25rem'
                    icon={<TbWebhook style={{ color: token.colorTextTertiary }} />}
                    label={query ? 'No Matching Webhooks' : 'No Webhooks'}
                    description={
                        query
                            ? 'No webhooks match the current search.'
                            : 'Create a webhook above to subscribe your applications to real-time Countercyclical events.'
                    }
                />
            )}
        </>
    );
};

export default Webhooks;
